import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Chip, ProgressBar, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatInt, isNum, dubaiCycleDay } from '../shared/format';
import DiamondAmount from '../shared/DiamondAmount';

/**
 * Module 1 — A1.3 Below-pace flag (mockup module1-performance.jsx:170-219).
 *
 * Flags each MTD volume metric as Behind / On pace against its linear MTD
 * target, using the clear `*_completion_pct_vs_mtd_pace` fields. Behind metrics
 * get an amber accent + warn icon. Diamonds are shown compact (no USD), matching
 * the mockup. The progress bar is red < 80% MTD pace, amber 80-100%, green ≥100%.
 *
 * The first below-pace metric carries the "Mark as today's top priority" button,
 * persisted per manager+day in `tcn_manager_daily_priority`. The state is owned
 * by the parent (TcnMeIndex) so OverviewMetricCard can show a matching
 * "★ PRIORITY" pill — props: `priorityKey`, `onTogglePriority`, `priorityLoading`.
 */
const PaceFlagsCard = ({ data, priorityKey, onTogglePriority, priorityLoading }) => {
  const { t } = useTranslation();
  const saving = !!priorityLoading;

  if (!data) return null;

  const metrics = [
    {
      key: 'diamonds',
      label: t('tcn.m1.overview.diamondsTitle', 'Diamonds'),
      completion: data.diamonds_completion_pct_vs_mtd_pace,
      actual: <DiamondAmount value={data.diamonds_mtd_actual_clear} />,
      target: <DiamondAmount value={data.diamonds_mtd_target_clear} />,
    },
    {
      key: 'recruited',
      label: t('tcn.m1.overview.recruitedTitle', 'Recruited creators'),
      completion: data.recruited_creators_completion_pct_vs_mtd_pace,
      actual: isNum(data.recruited_creators_mtd_actual)
        ? formatInt(data.recruited_creators_mtd_actual)
        : '—',
      target: isNum(data.recruited_creators_mtd_target)
        ? formatInt(Math.round(data.recruited_creators_mtd_target))
        : '—',
    },
  ];

  // Active days (any-LIVE days, daily injection only). Target = elapsed days ×
  // total creators ("if every creator went live every day"). Shown only once the
  // daily recompute has populated active_days_mtd_total (NULL/0 → hidden).
  const activeDaysActual = data.active_days_mtd_total;
  if (isNum(activeDaysActual) && activeDaysActual > 0) {
    const { day: elapsedDays } = dubaiCycleDay();
    const totalCreators = Number(data.total_creators_mine ?? 0);
    const activeDaysTarget = elapsedDays * totalCreators;
    metrics.push({
      key: 'active_days',
      label: t('tcn.m1.pace.activeDays', 'Active days'),
      completion: activeDaysTarget > 0 ? (activeDaysActual / activeDaysTarget) * 100 : null,
      actual: formatInt(activeDaysActual),
      target: activeDaysTarget > 0 ? formatInt(activeDaysTarget) : '—',
    });
  }

  // The mockup surfaces the "top priority" button on the first below-pace metric.
  const firstBehindKey = metrics.find(
    (m) => isNum(m.completion) && m.completion < 100
  )?.key;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {metrics.map((m) => {
        const known = isNum(m.completion);
        const behind = known && m.completion < 100;
        const barColor = !known
          ? undefined
          : m.completion >= 100
            ? 'green'
            : m.completion >= 80
              ? 'amber'
              : 'red';
        const showPriorityBtn = m.key === firstBehindKey;
        const isPriority = priorityKey === m.key;
        return (
          <Card
            key={m.key}
            style={{
              border: behind ? `1px solid ${TCN.amber}55` : `1px solid ${TCN.border}`,
              background: behind
                ? `linear-gradient(135deg, ${TCN.amber}10, ${TCN.surface1})`
                : TCN.surface1,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {behind && <Icon name="warn" size={14} color={TCN.amber} />}
                  <span style={{ fontSize: 14, fontWeight: 700, color: TCN.text }}>{m.label}</span>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: TCN.textDim,
                    marginTop: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexWrap: 'wrap',
                  }}
                >
                  {m.actual}
                  <span style={{ color: TCN.textMute }}>{t('tcn.m1.pace.ofWord', 'of')}</span>
                  {m.target}
                  <span style={{ color: TCN.textMute }}>{t('tcn.m1.pace.paceWord', 'pace')}</span>
                </div>
              </div>
              <Chip
                color={!known ? 'cyan' : behind ? 'amber' : 'green'}
                size="sm"
                solid={behind}
              >
                {!known
                  ? '—'
                  : behind
                    ? t('tcn.m1.pace.behind', 'Behind')
                    : t('tcn.m1.pace.onPace', 'On pace')}
              </Chip>
            </div>

            <div style={{ marginTop: 10 }}>
              <ProgressBar
                value={known ? m.completion : 0}
                target={100}
                color={barColor}
                showCap
              />
            </div>

            {showPriorityBtn && (
              <button
                type="button"
                onClick={() => onTogglePriority?.(m.key)}
                disabled={saving}
                style={{
                  marginTop: 12,
                  width: '100%',
                  padding: '10px',
                  borderRadius: 10,
                  background: isPriority ? 'transparent' : TCN.amber,
                  color: isPriority ? TCN.amber : '#000',
                  border: isPriority ? `1px solid ${TCN.amber}` : 'none',
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  cursor: saving ? 'default' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                }}
              >
                <Icon name={isPriority ? 'check' : 'flame'} size={14} color={isPriority ? TCN.amber : '#000'} />
                {isPriority
                  ? t('tcn.m1.pace.priorityMarked', "Today's top priority")
                  : t('tcn.m1.pace.markPriority', "Mark as today's top priority")}
              </button>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default PaceFlagsCard;
